import {Box, Skeleton} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import {type FC, useEffect, useState} from 'react';
import {MdKeyboardArrowLeft, MdKeyboardArrowRight} from 'react-icons/md';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GTAG_LOCATION} from '../../../config/constants/gtag';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {getDisplaySectionNameListForEntrySheet} from '../../../core/factories/reports/getDisplaySectionNameList';
import {useGetCompanyInfo} from '../../../core/repositories/companiesRepository';
import {useGraduationYearsHot} from '../../../core/repositories/recruitingInfoRepository';
import type {GetEntrySheetResponse} from '../../../core/repositories/reportsRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {SpHeaderBanner} from '../../../shared/components/Banner/SpHeaderBanner';
import {SpTopBottomBanner} from '../../../shared/components/Banner/SpTopBottomBanner';
import {CommonLink} from '../../../shared/components/CommonLink';
import {NoLoginAlertModal} from '../../../shared/components/NoLoginAlertModal';
import {SpFloatContent} from '../../../shared/components/SpFloatContent';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useWindowSize} from '../../../shared/services/useWindowSize';
import {RecommendRecrutments} from '../../reportAnalytics/components/Presenter/RecommendRecruitments';
import {listEntrySheetBaseInfo} from '../usecase/listEntrySheetBaseInfo';
import {listEntrySheetQuestionAnswer} from '../usecase/listEntrySheetQuestionAnswer';
import {useHandleClip} from '../usecase/useHandleClip';
import {GraduatedUserReportBox} from './Container/Presenter/GraduatedUserReportBox';
import {NoLoginReportModal} from './Container/Presenter/NoLoginReportModal';
import {PcQuestionList} from './Container/Presenter/PcQuestionList';
import {PcSectionTitle} from './Container/Presenter/PcSectionTitle';
import {ReportDetailHeader} from './Container/ReportDetailHeader';

type Props = {
  isLogin: boolean;
  entrySheet: GetEntrySheetResponse;
};

export const EntrySheetPage: FC<Props> = ({isLogin, entrySheet}) => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath.replace(/#.*$/, ''))}`;
  const title = `${entrySheet.companyName} ${entrySheet.department.name} ${
    entrySheet.graduationYear % 100
  }卒 本選考突破のエントリーシート`;

  const {isClipped, clipCountUpdated, handleClip} = useHandleClip({
    isLogin,
    reportId: entrySheet.entrysheetID,
    reportType: 'entrysheet',
    clipped: entrySheet.isClipped,
    clipCount: entrySheet.clipCount,
  });

  const {data: companyInfo} = useGetCompanyInfo({
    companyId: entrySheet.companyID,
  });

  const [hasRecruitments, setHasRecruitments] = useState<boolean>(false);

  const [opened, {open, close}] = useDisclosure(false);
  const handleClipOrOpenModal = async () => {
    if (isLogin) {
      await handleClip();
    } else {
      open();
    }
  };

  const entrySheetNameList: string[] =
    getDisplaySectionNameListForEntrySheet(entrySheet);

  // 現在の表示している章の検知に関する処理
  const [currentSection, setCurrentSection] = useState<number>(0);
  // InterctionObserver APIを使って実装すると、なぜかsection2の交差がなぜか検知されないため、eventListenerを使って実装
  useEffect(() => {
    const handleScroll = () => {
      const sections = [...document.querySelectorAll('section')];
      const topVisibleSections = sections.filter(
        (section) => section.getBoundingClientRect().bottom > 200,
      );
      const topVisibleSectionIndex =
        topVisibleSections && topVisibleSections.length > 0
          ? Number(topVisibleSections[0]?.id.split('-')[1])
          : -1;
      if (
        topVisibleSectionIndex >= 0 &&
        currentSection !== topVisibleSectionIndex
      ) {
        setCurrentSection(topVisibleSectionIndex);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentSection]);

  // DFPバナーのセット
  const googleAdBannerNames = [
    GoogleAdBannerName.B_RECTANGLE,
    GoogleAdBannerName.B_RECTANGLE_BOTTOM,
  ];
  useGoogleAdBanner(googleAdBannerNames);

  const {width: windowWidth} = useWindowSize();

  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const [
    isOpenTableOfContentsModal,
    {open: openTableOfContentsModal, close: closeTableOfContentsModal},
  ] = useDisclosure(false);

  const {data: graduationYearsHots} = useGraduationYearsHot({});
  const {user, isGraduatedUser} = useAuth();

  // 卒年が指定されていない&ログイン時&ユーザーの卒年がターゲット卒年に含まれている場合、デフォルトで卒年をセット
  const graduationYear =
    user &&
    graduationYearsHots?.ok &&
    graduationYearsHots.value.years.includes(user.graduationYear)
      ? user.graduationYear
      : undefined;

  return (
    <>
      <Head>
        <title key="title">{`${title} | 外資就活ドットコム`}</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta key="og:title" property="og:title" content={`${title}`} />
        <meta key="og:url" property="og:url" content={currentUrl} />
      </Head>
      <Box className="mx-auto flex pc:w-[1200px] w-full justify-between">
        <Box>
          {isValid && (
            <Box className="p-20 pc:hidden">
              <SpHeaderBanner location={GTAG_LOCATION.PASSED_ES} />
            </Box>
          )}
          <SpFloatContent openModal={openTableOfContentsModal} scrollY={200} />
          <ReportDetailHeader
            companyInfo={companyInfo}
            companyId={entrySheet.companyID}
            companyName={entrySheet.companyName}
            title={title}
            pageType={'entrysheet'}
            reportType={entrySheet.selectionReportType}
            isClipped={isClipped}
            clipCount={String(clipCountUpdated)}
            handleClipUpdate={handleClipOrOpenModal}
            sectionNameList={entrySheetNameList}
            existPreference={false}
            openTableOfContentsModal={openTableOfContentsModal}
            closeTableOfContentsModal={closeTableOfContentsModal}
            isOpenTableOfContentsModal={isOpenTableOfContentsModal}
            hasRecruitments={hasRecruitments}
          />
          <Box className="flex">
            <Box className="w-[370px] pc:inline-block hidden">
              <Box className="sticky top-[150px]">
                <Box className="text-md w-full rounded-[16px] shadow-card bg-white relative p-16">
                  <Box className="border-b-2 border-b-line font-bold">目次</Box>
                  <ul>
                    {entrySheetNameList.map((section_name, index) => (
                      <a
                        key={`section-${index}`}
                        className="hover:font-bold font-bold"
                        href={`#section-${index}`}
                        onClick={() => {
                          setCurrentSection(index);
                        }}
                      >
                        <li
                          key={`qa-title-${index}`}
                          className={`${
                            currentSection === index ? 'bg-background-2' : ''
                          } mt-12`}
                        >
                          {section_name}
                        </li>
                      </a>
                    ))}
                    {hasRecruitments && (
                      <li className="font-bold mt-12">
                        <a
                          href="#recommend-recruitments"
                          className="text-md hover:font-bold"
                        >
                          このESを見ている方におすすめの募集
                        </a>
                      </li>
                    )}
                  </ul>
                </Box>
              </Box>
            </Box>

            <Box className="w-full px-8 flex flex-col pc:max-w-[830px] pc:ml-12 pc:px-0">
              <section id="section-0">
                <PcSectionTitle sectionTitle="基本情報" />
                <PcQuestionList
                  label="base-info"
                  questionList={listEntrySheetBaseInfo(entrySheet)}
                />
                {!isLogin && (
                  <Box className="bg-white mt-[-26px] rounded-4 p-12">
                    <CommonLink
                      href={`/company/${entrySheet.companyID}/experiences`}
                      className="text-primary flex mt-32 w-[200px] font-bold"
                    >
                      この企業の体験記一覧
                      <MdKeyboardArrowRight size={24} />
                    </CommonLink>
                    <CommonLink
                      href={`/company/${entrySheet.companyID}`}
                      className="text-primary flex mt-8 w-[150px] font-bold"
                    >
                      企業研究ページ
                      <MdKeyboardArrowRight size={24} />
                    </CommonLink>
                  </Box>
                )}
              </section>
              <div className="mt-16 flex justify-center pc:hidden">
                <SpTopBottomBanner type={'top'} width={windowWidth} />
              </div>
              {isGraduatedUser ? (
                <Box className="mt-16">
                  <GraduatedUserReportBox />
                </Box>
              ) : (
                <>
                  {isLogin ? null : (
                    <NoLoginReportModal
                      title={title}
                      description={
                        entrySheet.questionAnswers[0]?.answer.slice(0, 100) ||
                        ''
                      }
                      reportType="entrysheet"
                    />
                  )}
                  {listEntrySheetQuestionAnswer(entrySheet).map((qa, index) => (
                    <section
                      className="mt-16 pc:mt-24"
                      key={`qa-wrap-${index}`}
                      id={`section-${index + 1}`}
                    >
                      <PcSectionTitle sectionTitle={`設問${index + 1}`} />
                      <Box className="flex flex-col bg-white rounded-t-[4px] rounded-b-[12px] text-black-1 mt-8 pc:p-24">
                        {/* 未ログインの時は1問目のみ表示する */}
                        <PcQuestionList
                          isHideLabel={!(isLogin || index === 0)}
                          label={`qa${index}`}
                          questionList={qa.question_list}
                        />
                        <Box className="whitespace-pre-wrap border-t border-t-background-1 px-20 py-28 tracking-wide text-black-1">
                          {/* 未ログインの時は1問目まで100文字表示する */}
                          {isLogin ? (
                            qa.answer
                          ) : index === 0 ? (
                            `${qa.answer.slice(0, 100)}...`
                          ) : (
                            <Skeleton height={20} animate={false} />
                          )}
                        </Box>
                      </Box>
                    </section>
                  ))}
                </>
              )}
              <div className="mt-16 flex justify-center pc:hidden">
                <SpTopBottomBanner type={'bottom'} width={windowWidth} />
              </div>

              <div className="mx-8 border-t border-t-background-1 pt-24 text-base text-black-2">
                <p>
                  当サイト内に掲載されているすべての文章・画像の無断転載、転用を禁止します。
                </p>
              </div>

              <div className="inline-flex justify-between mt-16">
                <div>
                  {entrySheet.previousEntrySheetID && (
                    <CommonLink
                      href={`/passed_es/${entrySheet.previousEntrySheetID}`}
                      className="text-primary font-bold rounded-[20px] border border-primary inline-flex px-16 py-8 items-center"
                    >
                      <MdKeyboardArrowLeft size={24} />
                      <span className="text-base">前のES</span>
                    </CommonLink>
                  )}
                </div>
                <div>
                  {entrySheet.nextEntrySheetID && (
                    <CommonLink
                      href={`/passed_es/${entrySheet.nextEntrySheetID}`}
                      className="bg-primary text-white font-bold rounded-[20px] inline-flex px-16 py-8 items-center"
                    >
                      <span className="text-base">次のES</span>
                      <MdKeyboardArrowRight size={24} />
                    </CommonLink>
                  )}
                </div>
              </div>

              <Box>
                <CommonLink
                  href={`/company/${entrySheet.companyID}/experiences`}
                  className="text-primary flex mt-32 w-[200px] font-bold"
                >
                  この企業の体験記一覧
                  <MdKeyboardArrowRight size={24} />
                </CommonLink>
                <CommonLink
                  href={`/company/${entrySheet.companyID}`}
                  className="text-primary flex mt-8 w-[150px] font-bold"
                >
                  企業研究ページ
                  <MdKeyboardArrowRight size={24} />
                </CommonLink>
              </Box>
              <RecommendRecrutments
                graduationYear={graduationYear}
                industryCategoryID={
                  companyInfo?.ok ? companyInfo.value.industryCategoryID : 0
                }
                setHasRecruitments={setHasRecruitments}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <NoLoginAlertModal opened={opened} onClose={close} />
    </>
  );
};
